import { useEffect } from "react";
import { inject } from "mobx-react";
import { isLeft } from "fp-ts/Either";
import type { Store } from "@inferno/renderer-shared-core";
import { ILog, getPaths, getMediaServerUrl, AuthorPagePayloadResolver } from "@ihr-radioedit/inferno-core";
import DOMPurify from "isomorphic-dompurify";
import type { Block_SitesItemBlock_Fragment, PublishRecordFragment } from "@ihr-radioedit/inferno-webapi";
import { Container, Heading, MagicLink } from "../../ui";
import { FacebookIcon, InstagramIcon, TiktokIcon, XIcon, YoutubeIcon } from "@inferno/renderer-shared-ui";
import { ExternalLinkIcon } from "../../components/icons/ExternalLinkIcon.component";
import { AuthorFrontMatter } from "./AuthorFrontMatter.component";
import { ResponsiveImage } from "../../components/ResponsiveImage.component";
import type { SrcSet } from "@inferno/renderer-shared-core";
import { RESPONSIVE_IMAGE } from "../../ui/constants";

import styles from "./AuthorContent.module.scss";
const log = ILog.logger("AuthorContent");

export type AuthorContentProps = {
  store?: Store;
  content: PublishRecordFragment;
  block: Block_SitesItemBlock_Fragment;
};

export const AuthorContent = inject("store")(({ content, block, store }: AuthorContentProps) => {
  if (!store || content === null) {
    return null;
  }

  useEffect(() => {
    store.storeBlock(block);
  });

  const resolvedPayload = AuthorPagePayloadResolver.decode(content.payload);
  if (isLeft(resolvedPayload)) {
    log.error(`Payload did not validate! ${getPaths(resolvedPayload)}`);
    return null;
  }
  const payload = resolvedPayload.right;
  const { fields } = payload;

  if (!fields) {
    return null;
  }

  const {
    name,
    bio,
    photo,
    link,
    author_facebook: facebook,
    author_instagram: instagram,
    author_tiktok: tiktok,
    author_twitter: twitter,
    youtube,
  } = fields;

  const { bucket = "", id = "" } = photo?.value ?? { bucket: "", id: "" };

  const imgUrl = getMediaServerUrl(store.env.IMAGE_HOST, bucket, id);

  const srcset: SrcSet[] = [
    {
      url: `${imgUrl}?ops=gravity(%22center%22),fit(180,180)`,
      descriptor: "1x",
    },
    {
      url: `${imgUrl}?ops=gravity(%22center%22),fit(360,360)`,
      descriptor: "2x",
    },
  ];

  const sanitizedData = () => ({
    __html: DOMPurify.sanitize(bio.value!),
  });

  return (
    <>
      {!block.tags?.includes("display-hints/no-front-matter") ? (
        <AuthorFrontMatter content={content} block={block} />
      ) : null}
      <Container className={styles.authorPage}>
        <div className={styles.authorLinks}>
          <figure className={styles.authorThumb}>
            <ResponsiveImage
              alt={""}
              src={`${imgUrl}?ops=gravity(%22center%22),fit(180,180)`}
              srcset={srcset}
              initialHeight={RESPONSIVE_IMAGE.authorThumbHeight}
              initialWidth={RESPONSIVE_IMAGE.authorThumbWidth}
              placeholder={`${imgUrl}?ops=gravity(%22center%22),fit(180,180)`}
              placeholderSrcset={srcset}
            />
          </figure>
          <ul className={styles.authorSocial}>
            {link?.value !== "" && (
              <li>
                <MagicLink to={link.value!} aria-label={`Website for ${name.value}.`} target="_blank">
                  <div className="icon-container">
                    <ExternalLinkIcon />
                  </div>
                  {link.value}
                </MagicLink>
              </li>
            )}
            {facebook?.value !== "" && (
              <li>
                <MagicLink
                  to={`https://www.facebook.com/${facebook!.value}`}
                  aria-label={`Follow ${name.value} on Facebook`}
                  target="_blank"
                >
                  <div className="icon-container">
                    <FacebookIcon />
                  </div>
                  @{facebook!.value}
                </MagicLink>
              </li>
            )}
            {twitter?.value !== "" && (
              <li>
                <MagicLink
                  to={`https://www.x.com/${twitter!.value}`}
                  aria-label={`Follow ${name.value} on X`}
                  target="_blank"
                >
                  <div className="icon-container">
                    <XIcon />
                  </div>
                  @{twitter!.value}
                </MagicLink>
              </li>
            )}
            {instagram?.value !== "" && (
              <li>
                <MagicLink
                  to={`https://www.instagram.com/${instagram!.value}`}
                  aria-label={`Follow ${name.value} on Instagram`}
                  target="_blank"
                >
                  <div className="icon-container">
                    <InstagramIcon />
                  </div>
                  @{instagram!.value}
                </MagicLink>
              </li>
            )}
            {tiktok?.value !== "" && (
              <li>
                <MagicLink
                  to={`https://www.tiktok.com/@${tiktok!.value}`}
                  aria-label={`Follow ${name.value} on TikTok`}
                  target="_blank"
                >
                  <div className="icon-container">
                    <TiktokIcon />
                  </div>
                  @{tiktok!.value}
                </MagicLink>
              </li>
            )}
            {youtube?.value !== "" && (
              <li>
                <MagicLink
                  to={`https://www.youtube.com/@${youtube!.value}`}
                  aria-label={`Subscribe to the ${name.value} on YouTube.`}
                  target="_blank"
                >
                  <div className="icon-container">
                    <YoutubeIcon />
                  </div>
                  @{youtube!.value}
                </MagicLink>
              </li>
            )}
          </ul>
        </div>
        <div className={styles.authorBio}>
          <Heading level={2}>About</Heading>
          <div className={styles.BioText} dangerouslySetInnerHTML={sanitizedData()} />
        </div>
      </Container>
    </>
  );
});

export default AuthorContent;
